import React, { useState, useEffect, useRef } from 'react';
import StyledQuill from '../shared/StyledQuill';
import { updateProject } from '../../utils/projectHandlers';
import { uploadFileToFirebase } from '../../utils/firebaseUploadUtils';
import ProjectSettingsMenu from '../ProjectSettingsMenu';
import UserProfileMenu from './UserProfileMenu';
import EditIcon from '../icons/EditIcon'; // Import the new EditIcon
import { SearchIcon } from '../icons/SearchIcon'; // Import SearchIcon as named export
import { FilterIcon } from '../icons/FilterIcon'; // Import FilterIcon
import { MenuIcon } from '../icons/MenuIcon'; // Import MenuIcon for mobile
import CloseIcon from '../icons/CloseIcon'; // Import CloseIcon for mobile sidebar and search clear
import styled from 'styled-components';
import {
    ProjectHeader as Header,
    ProjectTitleWrapper,
    TitleControlsLine, // Import the new styled component
    TitleSection,
    ProjectTitle,
    ProjectTagline,
    ProjectUrl,
    ProjectUrlDisplay, // Import the new styled component
    ProjectDescription, // Keep for read-only display
    HeaderControls,
    CollapseButton,
    CollapsedInfo,
    UrlWrapper,
    UrlDisplayWrapper, // Import the new styled component
    QuillEditorContainer, // Import the QuillEditorContainer style
    DescriptionWrapper, // New styled component for description area
    EditButton, // New styled component for the edit icon
    SearchInputWrapper, // New styled component for search input
    SearchInput, // New styled component for search input
} from '../../styles/ProjectPage.styles';
import FilterPopover from './FilterPopover';

// Mobile sidebar styled components
const MobileMenuButton = styled.button`
    background: transparent;
    border: none;
    color: #fff;
    padding: 8px;
    cursor: pointer;
    display: none;
    align-items: center;
    justify-content: center;
    
    @media (max-width: 768px) {
        display: flex;
    }
`;

const MobileSidebar = styled.div`
    position: fixed;
    top: 0;
    right: ${props => props.isOpen ? '0' : '-300px'};
    width: 280px;
    height: 100vh;
    background: #282E33;
    z-index: 1001;
    transition: right 0.3s ease;
    box-shadow: ${props => props.isOpen ? '-4px 0 10px rgba(0, 0, 0, 0.2)' : 'none'};
    padding: 16px;
    display: flex;
    flex-direction: column;
`;

const SidebarHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;
`;

const SidebarTitle = styled.h3`
    color: #fff;
    margin: 0;
    font-size: 16px;
    `;

const CloseButton = styled.button`
    background: transparent;
    border: none;
    color: #fff;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
`;

const SidebarContent = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
`;

const Overlay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    z-index: 1000;
    display: ${props => props.isOpen ? 'block' : 'none'};
`;

const ProjectHeader = ({
    project,
    isCollapsed,
    setIsCollapsed,
    background,
    onBackgroundChange,
    archivedCards,
    onCardClick,
    textareaHeight,
    handleTextAreaHeight,
    limitColumnHeight,
    handleToggleLimitColumnHeight,
    enableGithubLinks, // Add new prop
    handleToggleEnableGithubLinks, // Add new handler prop
    searchTerm,
    onSearchChange,
    filters,
    onFilterChange,
    labels,
    projectMembers,
    onExportProject
}) => {
    const [isEditing, setIsEditing] = useState(false); // General editing state for title, tagline
    const [isUrlEditing, setIsUrlEditing] = useState(false); // Specific state for URL
    const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false); // State for mobile sidebar
    const [isDescriptionEditing, setIsDescriptionEditing] = useState(false); // Specific state for description
    const [localTitle, setLocalTitle] = useState(project.title);
    const [localTagline, setLocalTagline] = useState(project.tagline || '');
    const [localUrl, setLocalUrl] = useState(project.url || '');
    const [localDescription, setLocalDescription] = useState(project.description || ''); // This will hold HTML content
    const [showFilterPopover, setShowFilterPopover] = useState(false);
    
    const quillRef = useRef(null);
    const taglineRef = useRef(null); // Ref for the tagline textarea

    const adjustTextareaHeight = (element) => {
        if (element) {
            element.style.height = 'auto'; // Reset height to recalculate scrollHeight
            element.style.height = `${element.scrollHeight}px`;
        }
    };

    useEffect(() => {
        setLocalTitle(project.title);
        setLocalTagline(project.tagline || '');
        setLocalUrl(project.url || '');
        setLocalDescription(project.description || '');
    }, [project]);

    // Adjust height on initial load and when tagline changes externally
    useEffect(() => {
        if (taglineRef.current) {
            adjustTextareaHeight(taglineRef.current);
        }
    }, [localTagline]);

    const handleTitleChange = (e) => {
        setLocalTitle(e.target.value);
    };

    const handleTaglineChange = (e) => {
        setLocalTagline(e.target.value);
        if (taglineRef.current) {
            adjustTextareaHeight(taglineRef.current);
        }
    };

    const handleUrlChange = (e) => {
        setLocalUrl(e.target.value);
    };

    const handleDescriptionChange = (content) => {
        setLocalDescription(content);
    };

    const handleTitleBlur = async () => {
        if (localTitle !== project.title) {
            await updateProject(project.id, { title: localTitle });
        }
    };

    const handleTaglineBlur = async () => {
        if (localTagline !== project.tagline) {
            await updateProject(project.id, { tagline: localTagline });
        }
    };

    const handleUrlBlur = async () => {
        if (localUrl !== project.url) {
            // Only update if URL is empty or valid
            if (!localUrl || isValidUrl(localUrl)) {
                await updateProject(project.id, { url: localUrl });
            } else {
                // Reset to previous valid URL if invalid
                setLocalUrl(project.url || '');
            }
        }
        setIsUrlEditing(false); // Exit editing mode after blur
    };

    const handleUrlEditClick = (e) => {
        e.stopPropagation(); // Prevent the UrlWrapper's click from propagating
        setIsUrlEditing(true);
    };

    const handleDescriptionSave = async () => {
        if (localDescription !== project.description) {
            await updateProject(project.id, { description: localDescription });
        }
        setIsDescriptionEditing(false);
    };

    const handleImageUpload = async (file) => {
        try {
            const attachment = await uploadFileToFirebase(file, 'project-editor-images');
            if (attachment) {
                const editor = quillRef.current.getEditor();
                const range = editor.getSelection();
                editor.insertEmbed(range.index, 'image', attachment.url);
                // Optionally, add to a project-level attachments array if needed
                // await updateProject(project.id, { attachments: [...(project.attachments || []), attachment] });
            }
        } catch (error) {
            console.error('Error uploading image:', error);
        }
    };

    const handlePaste = (e) => {
        const clipboard = e.clipboardData;
        if (clipboard.files.length > 0) {
            e.preventDefault();
            Array.from(clipboard.files).forEach(file => {
                if (file.type.startsWith('image/')) {
                    handleImageUpload(file);
                } else {
                    // Handle non-image files as links
                    uploadFileToFirebase(file, 'project-editor-files').then(attachment => {
                        if (attachment) {
                            const editor = quillRef.current.getEditor();
                            const range = editor.getSelection();
                            editor.insertText(range.index, attachment.name, { 'link': attachment.url });
                            // Optionally, add to a project-level attachments array if needed
                            // await updateProject(project.id, { attachments: [...(project.attachments || []), attachment] });
                        }
                    });
                }
            });
        }
    };

    const modules = {
        toolbar: [
            ['bold', 'italic', 'underline', 'strike'],
            [{ 'list': 'ordered' }, { 'list': 'bullet' }],
            ['link', 'image']
        ],
        clipboard: {
            matchVisual: false,
        },
    };

    const formats = [
        'bold', 'italic', 'underline', 'strike',
        'list', 'bullet',
        'link', 'image'
    ];

    const isValidUrl = (string) => {
        try {
            new URL(string);
            return true;
        } catch (_) {
            return false;
        }
    };

    const handleUrlClick = (e) => {
        // Only open URL if not in editing mode and a valid URL exists
        if (!isUrlEditing && isValidUrl(localUrl)) {
            // No e.preventDefault() here, as the anchor tag's default behavior is desired
            // The anchor tag itself will handle opening the URL
        } else {
            // If in editing mode or URL is invalid, prevent default navigation
            e.preventDefault();
        }
    };

    return (
        <>
            {/* Mobile Sidebar */}
            <Overlay isOpen={isMobileSidebarOpen} onClick={() => setIsMobileSidebarOpen(false)} />
            <MobileSidebar isOpen={isMobileSidebarOpen}>
                <SidebarHeader>
                    <SidebarTitle>Search & Filter</SidebarTitle>
                    <CloseButton onClick={() => setIsMobileSidebarOpen(false)}>
                        <CloseIcon />
                    </CloseButton>
                </SidebarHeader>
                <SidebarContent>
                    <div>
                        <SearchInputWrapper style={{ width: '100%', marginRight: 0 }}>
                            <SearchIcon />
                            <SearchInput
                                type="text"
                                placeholder="Search cards..."
                                value={searchTerm}
                                onChange={(e) => onSearchChange(e.target.value)}
                                style={{ width: '100%' }}
                            />
                            <CloseButton
                                onClick={() => onSearchChange('')}
                                style={{
                                    background: 'transparent',
                                    border: 'none',
                                    color: '#fff',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    padding: '0 4px',
                                    visibility: searchTerm ? 'visible' : 'hidden' // Control visibility
                                }}
                            >
                                <CloseIcon />
                            </CloseButton>
                        </SearchInputWrapper>
                    </div>
                    <div style={{ marginTop: '8px' }}>
                        <button
                            onClick={() => setShowFilterPopover(!showFilterPopover)}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                background: 'rgba(255, 255, 255, 0.2)',
                                border: 'none',
                                borderRadius: '3px',
                                padding: '8px 12px',
                                color: '#fff',
                                cursor: 'pointer',
                                width: '100%'
                            }}
                        >
                            <FilterIcon />
                            <span style={{ marginLeft: '8px' }}>Filter Cards</span>
                        </button>
                        {showFilterPopover && (
                            <FilterPopover
                                filters={filters}
                                onFilterChange={(newFilters) => {
                                    onFilterChange(newFilters);
                                    // Close the filter popover after selection on mobile
                                    if (window.innerWidth <= 768) {
                                        setShowFilterPopover(false);
                                        // Also close the sidebar after a short delay
                                        setTimeout(() => setIsMobileSidebarOpen(false), 300);
                                    }
                                }}
                                labels={labels}
                                members={projectMembers}
                                onClose={() => setShowFilterPopover(false)}
                            />
                        )}
                    </div>
                </SidebarContent>
            </MobileSidebar>
            
            <Header isCollapsed={isCollapsed}>
                <ProjectTitleWrapper isCollapsed={isCollapsed}>
                    <TitleSection isCollapsed={isCollapsed}>
                        <TitleControlsLine>
                            <ProjectTitle
                                value={localTitle}
                                onChange={handleTitleChange}
                                onBlur={handleTitleBlur}
                                readOnly={!isEditing}
                                onClick={() => setIsEditing(true)}
                                isCollapsed={isCollapsed} // This prop might be irrelevant now for styling if not used by ProjectTitle directly for padding
                                title={localTitle} // Add title attribute for tooltip
                            />
                            {/* HeaderControls are moved here, inside TitleControlsLine but outside TitleSection in original plan, fixing to be inside TitleSection -> TitleControlsLine */}
                            <HeaderControls>
                                <SearchInputWrapper className="desktop-only">
                                    <SearchIcon />
                                    <SearchInput
                                        type="text"
                                        placeholder="Search cards..."
                                        value={searchTerm}
                                        onChange={(e) => onSearchChange(e.target.value)}
                                    />
                                    <CloseButton
                                        onClick={() => onSearchChange('')}
                                        style={{
                                            background: 'transparent',
                                            border: 'none',
                                            color: '#fff',
                                            cursor: 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            padding: '0 4px',
                                            visibility: searchTerm ? 'visible' : 'hidden'
                                        }}
                                    >
                                        <CloseIcon />
                                    </CloseButton>
                                    <div style={{ position: 'relative', marginLeft: '8px' }}>
                                        <FilterIcon onClick={() => setShowFilterPopover(!showFilterPopover)} />
                                        {showFilterPopover && (
                                            <FilterPopover
                                                filters={filters}
                                                onFilterChange={(newFilters) => {
                                                    onFilterChange(newFilters);
                                                    if (window.innerWidth <= 768) {
                                                        setShowFilterPopover(false);
                                                    }
                                                }}
                                                labels={labels}
                                                members={projectMembers}
                                                onClose={() => setShowFilterPopover(false)}
                                            />
                                        )}
                                    </div>
                                </SearchInputWrapper>
                                <MobileMenuButton onClick={() => setIsMobileSidebarOpen(true)}>
                                    <MenuIcon />
                                </MobileMenuButton>
                                <ProjectSettingsMenu
                                    currentBackground={background}
                                    onBackgroundChange={onBackgroundChange}
                                    projectId={project.id}
                                    projectTitle={localTitle}
                                    archivedCards={archivedCards}
                                    onCardClick={onCardClick}
                                    limitColumnHeight={limitColumnHeight}
                                    onToggleLimitColumnHeight={handleToggleLimitColumnHeight}
                                    enableGithubLinks={enableGithubLinks}
                                    onToggleEnableGithubLinks={handleToggleEnableGithubLinks}
                                    onExportProject={onExportProject}
                                />
                                <CollapseButton
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setIsCollapsed(!isCollapsed);
                                    }}
                                    title={isCollapsed ? 'Expand' : 'Collapse'}
                                >
                                    {isCollapsed ? <ExpandIcon /> : <CollapseIcon />}
                                </CollapseButton>
                            </HeaderControls>
                        </TitleControlsLine>

                        {!isCollapsed && (
                            <>
                                <ProjectTagline
                                    ref={taglineRef}
                                    value={localTagline}
                                onChange={handleTaglineChange}
                                onBlur={handleTaglineBlur}
                                placeholder="Add a short tagline..."
                                readOnly={!isEditing}
                                onClick={() => setIsEditing(true)}
                                // Add a rows attribute for a sensible default before JS height adjustment
                                rows="1"
                            />
                            <UrlWrapper>
                                <UrlIcon />
                                {isUrlEditing || !localUrl ? (
                                    <ProjectUrl
                                        value={localUrl}
                                        onChange={handleUrlChange}
                                        onBlur={handleUrlBlur}
                                        placeholder="Add project URL..."
                                        readOnly={!isUrlEditing && localUrl}
                                    />
                                ) : (
                                    <UrlDisplayWrapper>
                                        <ProjectUrlDisplay
                                            href={isValidUrl(localUrl) ? localUrl : '#'}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            onClick={handleUrlClick}
                                        >
                                            {localUrl}
                                        </ProjectUrlDisplay>
                                        <EditButton onClick={handleUrlEditClick}>
                                            <EditIcon />
                                        </EditButton>
                                    </UrlDisplayWrapper>
                                )}
                            </UrlWrapper>
                            <DescriptionWrapper>
                                {isDescriptionEditing ? (
                                    <QuillEditorContainer>
                                        <StyledQuill
                                            ref={quillRef}
                                            theme="snow"
                                            value={localDescription}
                                            onChange={handleDescriptionChange}
                                            onBlur={handleDescriptionSave} // Save on blur
                                            modules={modules}
                                            formats={formats}
                                            onPaste={handlePaste}
                                            placeholder="Add a more detailed description..."
                                        />
                                    </QuillEditorContainer>
                                ) : (
                                    <ProjectDescription
                                        onClick={() => setIsDescriptionEditing(true)}
                                    >
                                        {localDescription ? (
                                            <div dangerouslySetInnerHTML={{ __html: localDescription }} />
                                        ) : (
                                            <span style={{ color: '#6B778C' }}>Add a more detailed description...</span>
                                        )}
                                        <EditButton onClick={(e) => { e.stopPropagation(); setIsDescriptionEditing(true); }}>
                                            <EditIcon />
                                        </EditButton>
                                    </ProjectDescription>
                                )}
                            </DescriptionWrapper>
                        </>
                    )}
                </TitleSection>
            </ProjectTitleWrapper>
            {isCollapsed && localTagline && (
                <CollapsedInfo title={localTagline}>
                    <span>{localTagline}</span>
                </CollapsedInfo>
            )}
        </Header>
        </>
    );
};

const UrlIcon = () => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
        <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
    </svg>
);

const ExpandIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <polyline points="6 9 12 15 18 9" />
    </svg>
);

const CollapseIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <polyline points="18 15 12 9 6 15" />
    </svg>
);

export default ProjectHeader;
